import { z } from "zod";
import type { AcCardStatus } from "./accreditation";
import { formatCardNumber } from "./accreditation";
import type { AccessCodeKind } from "./access-codes";
import type { RoleCode } from "./auth";
import { keysetQuery, PAGE_SIZE_MAX, searchParam } from "./pagination";

/**
 * M7a E-Accreditation, slice 1 (migration 0036, user decisions 2026-10-16).
 * Wire format: snake_case.
 *
 * The PHYSICAL card's life is `ac_cards.card_state`, apart from the workflow
 * `status`. It exists only once a card is approved:
 *
 *   status becomes APPROVED  → NOT_PRINTED   (from NULL, or from VOID on a re-approval)
 *   status leaves APPROVED   → VOID          (from any state; its live issue is voided too)
 *
 * Both moves are made by the database (`fn_ac_card_state_guard`, the same
 * BEFORE trigger that guards the edges below), psql included. A CHECK holds
 * `status = 'APPROVED'` ⇔ `card_state` is set and not VOID.
 *
 * Every other move must be an edge of CARD_STATE_TRANSITIONS — the trigger
 * refuses the rest with 23514 `ck_ac_cards_card_state_transition` (→ 409
 * CARD_STATE_INVALID). tests/unit/card-state-guard.test.ts holds the
 * trigger's edge list equal to this one.
 *
 * LOST → the state before it is NOT an edge: it is the UNDO of an incident,
 * within CARD_INCIDENT_UNDO_MINUTES, and only the incident's own trigger can
 * take it (tx-local `app.card_state_undo`, the incident's stored
 * `previous_state`).
 *
 * The print pipeline (migration 0075, the user's SOP 2026-10-31): an
 * approved card waits as it is (NOT_PRINTED) until a host puts it in a print
 * batch → READY_TO_GENERATE; the batch's ZIP of card PNGs is drawn IN THE
 * BROWSER and the API is told once every card of the batch was drawn →
 * GENERATED (the batch stamps who and when); the host sends the batch to the
 * print shop → PRINTING; it comes back → PRINTED ("ready to send /
 * collect"); the NOC receives it → COLLECTED ("received"); activation →
 * ACTIVE. A SELF_PRINT batch (the host lets the NOC print its own cards)
 * stops at GENERATED — the NOC downloaded it — and goes straight to ACTIVE:
 * GENERATED → ACTIVE is taken only by a card whose live issue is in a
 * SELF_PRINT batch (the trigger checks it, 23514
 * `ck_ac_cards_card_state_self_print`).
 *
 * PRINTED and COLLECTED keep their 0036 names on purpose: audit rows are
 * append-only and already say PRINTED, and COLLECTED had no way in until
 * now. The words people read ("Ready to send / collect", "Received") are the
 * GMS's.
 */

// ── Card states ─────────────────────────────────────────────────────────────

export const CARD_STATES = [
	"NOT_PRINTED",
	"READY_TO_GENERATE",
	"GENERATED",
	"PRINTING",
	"PRINTED",
	"COLLECTED",
	"ACTIVE",
	"LOST",
	"REPLACED",
	"VOID",
] as const;
export type CardState = (typeof CARD_STATES)[number];

/** The state an approved card starts in, and the one a card leaving APPROVED ends in. Set by the database. */
export const CARD_STATE_ON_APPROVE = "NOT_PRINTED" as const satisfies CardState;
export const CARD_STATE_ON_LEAVE_APPROVED = "VOID" as const satisfies CardState;

/**
 * The direct edges (from → to) any writer may take, the API's and psql's
 * alike. PRINTED → ACTIVE stays: a desk may activate at handover without
 * marking the card received first. GENERATED → ACTIVE is for self-printed
 * cards only (above). REPLACED has no way in yet: the replacement slice adds
 * LOST → REPLACED with the new issue.
 */
export const CARD_STATE_TRANSITIONS = {
	NOT_PRINTED: ["READY_TO_GENERATE"],
	READY_TO_GENERATE: ["GENERATED"],
	GENERATED: ["PRINTING", "ACTIVE"],
	PRINTING: ["PRINTED"],
	PRINTED: ["COLLECTED", "ACTIVE", "LOST"],
	COLLECTED: ["ACTIVE", "LOST"],
	ACTIVE: ["LOST"],
	LOST: [],
	REPLACED: [],
	VOID: [],
} as const satisfies Record<CardState, readonly CardState[]>;

/** The (from, to) pairs of CARD_STATE_TRANSITIONS — what the database trigger allows, whoever asks. */
export const cardStateTransitionPairs = (): Array<[CardState, CardState]> =>
	CARD_STATES.flatMap((from) => (CARD_STATE_TRANSITIONS[from] as readonly CardState[]).map((to): [CardState, CardState] => [from, to]));

export const cardStateCanMove = (from: CardState, to: CardState): boolean => (CARD_STATE_TRANSITIONS[from] as readonly CardState[]).includes(to);

/** The states an incident can be reported from (they have an edge to LOST); the incident stores which, for the undo. */
export const CARD_INCIDENT_FROM_STATES = CARD_STATES.filter((s) => cardStateCanMove(s, "LOST"));

/** States that activation accepts (→ ACTIVE). GENERATED only for a self-printed card — the writing statement and the trigger both check the batch kind. */
export const CARD_ACTIVATABLE_STATES = CARD_STATES.filter((s) => cardStateCanMove(s, "ACTIVE"));

/** In a print batch, not back from the printer yet: what activation reports as `not_printed` (a self-printed GENERATED card aside). */
export const CARD_IN_PRODUCTION_STATES = ["READY_TO_GENERATE", "GENERATED", "PRINTING"] as const satisfies readonly CardState[];

// ── Incidents ───────────────────────────────────────────────────────────────

export const CARD_INCIDENT_KINDS = ["LOST", "STOLEN", "DAMAGED"] as const;
export type CardIncidentKind = (typeof CARD_INCIDENT_KINDS)[number];

/**
 * An incident can be undone this long after it was reported (the card goes
 * back to `previous_state`). The database stamps `undo_until = now() + this`
 * itself (0036 `fn_card_incident_guard`; pinned by card-state-guard.test.ts)
 * and refuses a later undo with 23514 `ck_card_incidents_undo_expired`
 * (→ 409 INCIDENT_UNDO_EXPIRED).
 */
export const CARD_INCIDENT_UNDO_MINUTES = 10;

export const CARD_INCIDENT_PLACE_MAX = 200;
export const CARD_INCIDENT_NOTE_MAX = 1000;
export const CARD_POLICE_REPORT_NO_MAX = 100;

// ── Issues (one printed card) ───────────────────────────────────────────────

/**
 * Why an issue stopped being the live one:
 *   CARD_VOID  the card left APPROVED (set by the database)
 *   REPLACED   a replacement was issued (the replacement slice)
 */
export const CARD_ISSUE_VOID_REASONS = ["CARD_VOID", "REPLACED"] as const;
export type CardIssueVoidReason = (typeof CARD_ISSUE_VOID_REASONS)[number];

/** `card_issues.qr_token`: 128 random bits as 32 lowercase hex (the DB default `fn_card_qr_token()`). */
export const CARD_QR_TOKEN_PATTERN = /^[0-9a-f]{32}$/;

// ── Batches ─────────────────────────────────────────────────────────────────

/** Cards per `POST /print-batches` and per `POST /cards/activate` (ids, or a filter's expected_count). */
export const PRINT_BATCH_MAX = 500;
export const CARD_ACTIVATE_MAX = 500;

/**
 * Who prints a batch (0075, decided by the host per batch):
 *   HOST        the host takes the ZIP to its print shop (generate → send → back → received)
 *   SELF_PRINT  the batch's NOC downloads the ZIP and prints its own cards; the
 *               batch holds ONE organisation's cards (`responsible_organization_id`,
 *               the card-issue trigger refuses another's) and stops at GENERATED
 */
export const PRINT_BATCH_KINDS = ["HOST", "SELF_PRINT"] as const;
export type PrintBatchKind = (typeof PRINT_BATCH_KINDS)[number];

/**
 * A batch's production run (0075), each step stamped with who and when on the
 * batch, its cards moved in the SAME statement:
 *   OPEN       made; its cards READY_TO_GENERATE
 *   GENERATED  every card drawn into the ZIP (`POST …/generated`); cards GENERATED
 *   PRINTING   sent to the print shop (`POST …/send-to-printer`); cards PRINTING — HOST batches only
 *   PRINTED    back from the printer (`POST …/mark-printed`); cards PRINTED (ready to send / collect)
 * Receiving is per card (`POST …/mark-received`, PRINTED → COLLECTED), not a batch step:
 * one batch serves several NOCs, each collecting on its own day.
 */
export const PRINT_BATCH_STATUSES = ["OPEN", "GENERATED", "PRINTING", "PRINTED"] as const;
export type PrintBatchStatus = (typeof PRINT_BATCH_STATUSES)[number];

/**
 * The batch status edges — AND `fn_print_batch_status_guard` (0075), held equal
 * by tests/unit/card-state-guard.test.ts. A SELF_PRINT batch never leaves
 * GENERATED (CHECK `ck_print_batches_self_print`).
 */
export const PRINT_BATCH_TRANSITIONS = {
	OPEN: ["GENERATED"],
	GENERATED: ["PRINTING"],
	PRINTING: ["PRINTED"],
	PRINTED: [],
} as const satisfies Record<PrintBatchStatus, readonly PrintBatchStatus[]>;

export const printBatchTransitionPairs = (): Array<[PrintBatchStatus, PrintBatchStatus]> =>
	PRINT_BATCH_STATUSES.flatMap((from) => (PRINT_BATCH_TRANSITIONS[from] as readonly PrintBatchStatus[]).map((to): [PrintBatchStatus, PrintBatchStatus] => [from, to]));

/** The card state each batch status moves its cards INTO (OPEN: on creation). */
export const PRINT_BATCH_CARD_STATE = {
	OPEN: "READY_TO_GENERATE",
	GENERATED: "GENERATED",
	PRINTING: "PRINTING",
	PRINTED: "PRINTED",
} as const satisfies Record<PrintBatchStatus, CardState>;

/** Self-print batch labels: `SP-<ORG CODE>-yyyymmdd-nnn` (no host username: the NOC reads it). */
export const SELF_PRINT_BATCH_PREFIX = "SP";

/**
 * The PNG the browser draws per card (`/print-batches/:id` generator): a
 * portrait CR80 card, 54 × 85.6 mm, at 300 dpi → 638 × 1011 px. The print
 * shop prints the file at that physical size.
 */
export const CARD_PRINT_WIDTH_MM = 54;
export const CARD_PRINT_HEIGHT_MM = 85.6;
export const CARD_PRINT_DPI = 300;
export const cardPrintPixels = (): { width: number; height: number } => ({
	width: Math.round((CARD_PRINT_WIDTH_MM / 25.4) * CARD_PRINT_DPI),
	height: Math.round((CARD_PRINT_HEIGHT_MM / 25.4) * CARD_PRINT_DPI),
});

/** Cards per page of `GET /print-batches/:id/cards` (the generator walks a batch of ≤ PRINT_BATCH_MAX in these pages). */
export const PRINT_BATCH_CARDS_PAGE_MAX = 100;

/**
 * A card's file name inside the ZIP: `<card number>_<PRINTED NAME>.png` —
 * the 6-digit number as it prints (`formatCardNumber`) and the name reduced
 * to A–Z, 0–9, `-` and `_` (accents stripped, anything else → `_`, at most
 * 60), so every OS and print-shop tool reads it. The number keeps it unique.
 */
export function cardPngFileName(registrationNo: number, printedName: string): string {
	const name = printedName
		.normalize("NFKD")
		.replace(/[̀-ͯ]/g, "")
		.toUpperCase()
		.replace(/[^A-Z0-9-]+/g, "_")
		.replace(/^_+|_+$/g, "")
		.slice(0, 60)
		.replace(/_+$/g, "");
	return `${formatCardNumber(registrationNo)}${name ? `_${name}` : ""}.png`;
}

/**
 * Why a card was left out of a print batch:
 *   not_in_scope     the caller cannot see it, or it does not exist
 *   not_printable    back from the printer or later (printed, received, active, lost, void)
 *   in_batch         it is already in a batch (READY_TO_GENERATE … PRINTING, or a live issue)
 *   no_photo         it has no photo (a card cannot print without one)
 */
export const PRINT_BATCH_SKIP_REASONS = ["not_in_scope", "not_printable", "in_batch", "no_photo"] as const;
export type PrintBatchSkipReason = (typeof PRINT_BATCH_SKIP_REASONS)[number];

/**
 * Why a card was left out of an activation:
 *   not_in_scope     the caller cannot see it, or it does not exist
 *   not_printed      NOT_PRINTED — print it first
 *   already_active   ACTIVE already
 *   lost             LOST
 *   void             VOID / REPLACED
 */
export const CARD_ACTIVATE_SKIP_REASONS = ["not_in_scope", "not_printed", "already_active", "lost", "void"] as const;
export type CardActivateSkipReason = (typeof CARD_ACTIVATE_SKIP_REASONS)[number];

/**
 * Batch labels: `<PREFIX>-<USERNAME>-yyyymmdd-nnn`, nnn per user per day
 * (games time zone), from 001. Activation: `AC-…`; print: `PB-…`. The
 * username is upper-cased, anything outside A–Z / 0–9 dropped.
 */
export const ACTIVATION_BATCH_PREFIX = "AC";
export const PRINT_BATCH_PREFIX = "PB";
export function formatBatchLabel(prefix: string, username: string, day: string, seq: number): string {
	const user = username.toUpperCase().replace(/[^A-Z0-9]/g, "") || "USER";
	return `${prefix}-${user}-${day.replace(/-/g, "")}-${String(seq).padStart(3, "0")}`;
}

// ── The card journey (the stepper on the record and the card page) ──────────

/**
 * Which stepper a viewer gets (0075) — decided by the API from the viewer's
 * ACTIVE role and the card's live batch kind, never by the web:
 *   HOST        admins and host groups: every production step
 *   NOC         an ORGANIZATION login, host-printed card: "Ready to generate" and
 *               "Generated" fold into "Printing in progress"
 *   SELF_PRINT  a card in a SELF_PRINT batch: Approved → Download → Activated
 *               (the host sees "Ready to generate" before the NOC's download)
 */
export const CARD_JOURNEY_VIEWS = ["HOST", "NOC", "SELF_PRINT"] as const;
export type CardJourneyView = (typeof CARD_JOURNEY_VIEWS)[number];

export const CARD_JOURNEY_STEPS = [
	"draft",
	"registered",
	"submitted",
	"underReview",
	"returned",
	"approved",
	"rejected",
	"readyToGenerate",
	"generated",
	"printing",
	"readyToSend",
	"readyToCollect",
	"received",
	"download",
	"activated",
	"lost",
] as const;
export type CardJourneyStep = (typeof CARD_JOURNEY_STEPS)[number];

export type CardJourneyTone = "ok" | "warn" | "fail" | "off";

/**
 * The stepper as the API sends it: the steps in order, `current` = the step
 * the card is AT (0-based; `steps.length` = every step done), the tone of
 * the current step (amber Returned, red Rejected / Lost, nothing lit for
 * Withdrawn), and the state it was read from (for the hint line).
 */
export interface CardJourney {
	view: CardJourneyView;
	steps: CardJourneyStep[];
	current: number;
	tone: CardJourneyTone;
	card_state: CardState | null;
}

/** The view for a viewer and a card's live batch kind (null = no batch yet). */
export function cardJourneyView(activeRole: RoleCode, batchKind: PrintBatchKind | null): CardJourneyView {
	if (batchKind === "SELF_PRINT") return "SELF_PRINT";
	return activeRole === "ORGANIZATION" ? "NOC" : "HOST";
}

/** Each view's production steps (after Approved), and where each card state stands on them. */
const PRODUCTION: Record<CardJourneyView, { steps: CardJourneyStep[]; at: Partial<Record<CardState, number>> }> = {
	HOST: {
		steps: ["readyToGenerate", "generated", "printing", "readyToSend", "received", "activated"],
		at: { READY_TO_GENERATE: 0, GENERATED: 1, PRINTING: 2, PRINTED: 3, COLLECTED: 4 },
	},
	NOC: {
		steps: ["printing", "readyToCollect", "received", "activated"],
		at: { READY_TO_GENERATE: 0, GENERATED: 0, PRINTING: 0, PRINTED: 1, COLLECTED: 2 },
	},
	SELF_PRINT: {
		steps: ["download", "activated"],
		at: { READY_TO_GENERATE: 0, GENERATED: 1, PRINTING: 1, PRINTED: 1, COLLECTED: 1 },
	},
};

/**
 * The stepper of one card for one view. The first three steps follow the
 * workflow status (as before 0075); an APPROVED card's physical state then
 * walks the view's production steps: NOT_PRINTED stays at Approved, ACTIVE
 * completes the rail, LOST / REPLACED ring the last step red as "Card lost".
 * A host's SELF_PRINT view adds "Ready to generate" before the download.
 */
export function cardJourney(status: AcCardStatus, cardState: CardState | null, view: CardJourneyView, activeRole?: RoleCode): CardJourney {
	const state = status === "APPROVED" ? cardState : null;
	const head: CardJourneyStep[] = [
		status === "DRAFT" ? "draft" : "registered",
		status === "PENDING" ? "underReview" : status === "RETURN" ? "returned" : "submitted",
		status === "REJECTED" ? "rejected" : "approved",
	];
	const prod = PRODUCTION[view];
	const hostSelfPrint = view === "SELF_PRINT" && activeRole !== undefined && activeRole !== "ORGANIZATION";
	const tail: CardJourneyStep[] = hostSelfPrint ? ["readyToGenerate", ...prod.steps] : [...prod.steps];
	const lost = state === "LOST" || state === "REPLACED";
	if (lost) tail[tail.length - 1] = "lost";
	const steps = [...head, ...tail];

	let current = { DRAFT: 0, SUBMITTED: 1, PENDING: 1, RETURN: 1, APPROVED: 2, REJECTED: 2, WITHDRAWN: 0 }[status];
	let tone: CardJourneyTone = status === "RETURN" ? "warn" : status === "REJECTED" ? "fail" : status === "WITHDRAWN" ? "off" : "ok";
	if (state === "ACTIVE") current = steps.length;
	else if (lost) {
		current = steps.length - 1;
		tone = "fail";
	} else if (state !== null) {
		const at = prod.at[state];
		// A host's self-print: READY_TO_GENERATE sits on its own step, the download moves one along.
		if (at !== undefined) current = head.length + at + (hostSelfPrint && state !== "READY_TO_GENERATE" ? 1 : 0);
	}
	return { view, steps, current, tone, card_state: state };
}

// ── Queries ─────────────────────────────────────────────────────────────────

const uuid = z.string().uuid();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD");

const splitList = (v: string) =>
	v
		.split(",")
		.map((s) => s.trim())
		.filter((s) => s.length > 0);

/** `card_state=PRINTED,ACTIVE` — one or more, comma-separated, de-duplicated. */
const cardStateList = z
	.string()
	.max(200)
	.transform(splitList)
	.pipe(z.array(z.enum(CARD_STATES)).min(1))
	.transform((list) => [...new Set(list)]);

/**
 * `GET /cards` — approved cards (and VOID ones when asked), keyset by
 * registration number, newest first. Total on the first page only (a scalar
 * count in the same statement, CLAUDE.md Pagination). `q` = card number
 * (`parseCardNumberQuery`), a QR token, or a name.
 */
export const cardsListQuery = keysetQuery.extend({
	games_id: uuid,
	card_state: cardStateList.optional(),
	responsible_organization_id: uuid.optional(),
	main_function_id: uuid.optional(),
	sport_id: uuid.optional(),
	print_batch_id: uuid.optional(),
	q: searchParam.optional(),
	limit: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(25),
});
export type CardsListQuery = z.infer<typeof cardsListQuery>;
export type CardsListQueryInput = z.input<typeof cardsListQuery>;

/** The list filter without paging: "select all N" for print / activate. */
export const cardsFilter = cardsListQuery.omit({ cursor: true, limit: true });
export type CardsFilter = z.input<typeof cardsFilter>;

/** `GET /cards/summary` — one `count(*) FILTER` per state. */
export const cardsSummaryQuery = z.object({
	games_id: uuid,
	responsible_organization_id: uuid.optional(),
});
export type CardsSummaryQuery = z.infer<typeof cardsSummaryQuery>;

/** `GET /cards/lookup?q=` — a QR token or a card number; one card or 404. */
export const cardLookupQuery = z.object({
	q: z.string().trim().min(1).max(100),
	games_id: uuid.optional(),
});
export type CardLookupQuery = z.infer<typeof cardLookupQuery>;

/** `GET /print-batches`, `GET /activation-batches` — keyset by created_at, newest first. */
export const cardBatchesQuery = keysetQuery.extend({
	games_id: uuid,
});
export type CardBatchesQuery = z.infer<typeof cardBatchesQuery>;

/** `GET /print-batches/:id/cards` — the generator's walk of one batch: its live cards, keyset by card number ascending; total on the first page. */
export const printBatchCardsQuery = keysetQuery.extend({
	limit: z.coerce.number().int().min(1).max(PRINT_BATCH_CARDS_PAGE_MAX).default(PRINT_BATCH_CARDS_PAGE_MAX),
});
export type PrintBatchCardsQuery = z.infer<typeof printBatchCardsQuery>;

// ── Bodies ──────────────────────────────────────────────────────────────────

/**
 * Either explicit `ids`, or "Select all N": the list's `filter` plus the
 * `expected_count` the user saw — the server re-counts in scope inside the
 * transaction (409 BULK_FILTER_CHANGED on a different number).
 */
const targetFields = (max: number) => ({
	ids: z.array(uuid).min(1).max(max).optional(),
	filter: cardsFilter.optional(),
	expected_count: z.number().int().min(1).max(max).optional(),
});
const oneTarget = (v: { ids?: unknown; filter?: unknown; expected_count?: unknown }) =>
	v.ids !== undefined ? v.filter === undefined && v.expected_count === undefined : v.filter !== undefined && v.expected_count !== undefined;
const distinctIds = (v: { ids?: string[] }) => v.ids === undefined || new Set(v.ids.map((id) => id.toLowerCase())).size === v.ids.length;

/**
 * `POST /print-batches` — creates one live issue per printable card (state
 * NOT_PRINTED, photo set, no live issue) and moves it to READY_TO_GENERATE.
 * `kind` SELF_PRINT (the NOC prints): every selected card must be of ONE
 * organisation (422 on `ids` otherwise).
 */
export const createPrintBatchBody = z
	.object({ ...targetFields(PRINT_BATCH_MAX), kind: z.enum(PRINT_BATCH_KINDS).default("HOST"), notes: z.string().trim().min(1).max(500).optional() })
	.refine(oneTarget, { path: ["ids"], message: "Send ids, or a filter with expected_count" })
	.refine(distinctIds, { path: ["ids"], message: "Each card may appear once" });
export type CreatePrintBatchBody = z.input<typeof createPrintBatchBody>;

/**
 * `POST /print-batches/:id/generated` — the browser drew every card of the
 * batch into its ZIP: `expected_count` = the cards it drew; a different number
 * of live cards in the batch → 409 BULK_FILTER_CHANGED, nothing written.
 */
export const printBatchGeneratedBody = z.object({ expected_count: z.number().int().min(1).max(PRINT_BATCH_MAX) });
export type PrintBatchGeneratedBody = z.input<typeof printBatchGeneratedBody>;

/** `POST /print-batches/:id/mark-received` — the batch's PRINTED cards → COLLECTED; only one organisation's when given. */
export const printBatchReceivedBody = z.object({ responsible_organization_id: uuid.optional() });
export type PrintBatchReceivedBody = z.input<typeof printBatchReceivedBody>;

/** `POST /cards/activate` — PRINTED / COLLECTED → ACTIVE, valid until `valid_until` (default: the games' `ends_on`). */
export const activateCardsBody = z
	.object({ ...targetFields(CARD_ACTIVATE_MAX), valid_until: isoDate.optional() })
	.refine(oneTarget, { path: ["ids"], message: "Send ids, or a filter with expected_count" })
	.refine(distinctIds, { path: ["ids"], message: "Each card may appear once" });
export type ActivateCardsBody = z.input<typeof activateCardsBody>;

/** `POST /cards/:id/activate` — one card, the same statement as the bulk activate. Refused (404 / 409 CARD_STATE_INVALID with `details.reason`) instead of skipped. */
export const activateCardBody = z.object({ valid_until: isoDate.optional() });
export type ActivateCardBody = z.input<typeof activateCardBody>;

export interface ReportCardIncidentMessages {
	policeReportRequired: string;
	/** `occurred_on` is a day that has not come yet. */
	occurredOnFuture: string;
}

export const enReportCardIncidentMessages: ReportCardIncidentMessages = {
	policeReportRequired: "A stolen card needs the police report number",
	occurredOnFuture: "The date cannot be in the future",
};

/**
 * The latest calendar day anywhere right now (UTC+14): a schema cannot know
 * the games' time zone, so it refuses only what is in the future everywhere;
 * the API re-checks against today in the games' zone (422 on `occurred_on`).
 */
export const latestTodayAnywhere = (now: Date = new Date()): string => new Date(now.getTime() + 14 * 3_600_000).toISOString().slice(0, 10);

/**
 * `POST /cards/:id/incidents` — the card goes LOST (from PRINTED / COLLECTED /
 * ACTIVE). A STOLEN card needs the police report number; `occurred_on` is not
 * in the future. Message factory for the web's localized form.
 */
export function makeReportCardIncidentBody(m: ReportCardIncidentMessages = enReportCardIncidentMessages) {
	return z
		.object({
			kind: z.enum(CARD_INCIDENT_KINDS),
			occurred_on: isoDate.refine((d) => d <= latestTodayAnywhere(), m.occurredOnFuture),
			place: z.string().trim().min(1).max(CARD_INCIDENT_PLACE_MAX).optional(),
			police_report_no: z.string().trim().min(1).max(CARD_POLICE_REPORT_NO_MAX).optional(),
			note: z.string().trim().min(1).max(CARD_INCIDENT_NOTE_MAX).optional(),
		})
		.refine((v) => v.kind !== "STOLEN" || v.police_report_no !== undefined, { path: ["police_report_no"], message: m.policeReportRequired });
}
export const reportCardIncidentBody = makeReportCardIncidentBody();
export type ReportCardIncidentBody = z.input<typeof reportCardIncidentBody>;

// ── Responses ───────────────────────────────────────────────────────────────

export interface CardRef {
	id: string;
	name: string;
}

/**
 * The card's LATEST issue: the live one, else the last voided one (a VOID
 * card still shows when it was printed; `voided_at` set, `qr_token` null).
 */
export interface CardIssue {
	card_issue_id: string;
	issue_no: number;
	/** What the QR encodes. Only for admins and host groups holding `card.print` or `card.activate` over the card; null for everyone else (a NOC never gets it). */
	qr_token: string | null;
	/** Null in the NOC view (ORGANIZATION): a host batch's name embeds the host's username — except a SELF_PRINT batch, which the NOC downloads. */
	print_batch_id: string | null;
	print_batch_name: string | null;
	print_batch_kind: PrintBatchKind;
	printed_at: string | null;
	created_at: string;
	/** Set when this issue is no longer the live one (the card left APPROVED). */
	voided_at: string | null;
}

export interface CardIncident {
	card_incident_id: string;
	kind: CardIncidentKind;
	occurred_on: string;
	place: string | null;
	police_report_no: string | null;
	note: string | null;
	previous_state: CardState;
	/** In the NOC view (ORGANIZATION) only when the reporter is of the caller's own group — never a host user. */
	reported_by: CardRef | null;
	reported_at: string;
	undo_until: string;
	undone_at: string | null;
	/** Still inside the undo window and not undone (the caller's permission aside). */
	can_undo: boolean;
}

/** One row of `GET /cards`. */
export interface CardListItem {
	ac_card_id: string;
	games_id: string;
	registration_no: number;
	registration_no_display: string;
	accreditation_no_display: string | null;
	card_state: CardState;
	printed_name: string;
	photo_url: string | null;
	organization: CardRef & { code: string | null };
	category: CardRef & { code: string; card_color: string | null };
	/** The card's function (sub function), when set. */
	sub_function: CardRef | null;
	sport: CardRef | null;
	valid_until: string | null;
	activated_at: string | null;
	/** The latest issue's batch (`CardIssue`), when there is one. Always null in the NOC view (ORGANIZATION): its name embeds the host's username. */
	print_batch: CardRef | null;
	printed_at: string | null;
}

export interface CardsListPage {
	items: CardListItem[];
	next_cursor: string | null;
	/** First page (no cursor) only, else null. */
	total: number | null;
}

/** `GET /cards/summary`. Counts are values, never keys (`ok()` lower-cases all-caps keys). */
export interface CardsSummary {
	games_id: string;
	/** Approved cards (every state but VOID). */
	total: number;
	/** PRINTED + COLLECTED: printed, waiting for activation. */
	to_activate: number;
	by_state: Array<{ state: CardState; count: number }>;
}

/** `GET /cards/:id` — one statement: card, live issue, live access, last incident. */
export interface CardDetail extends CardListItem {
	/** Always null in the NOC view (ORGANIZATION) — a NOC learns when, never who. */
	activated_by: CardRef | null;
	issue: CardIssue | null;
	/** The card's access codes (active ones), in kind then display order. */
	access: Array<{ access_code_id: string; kind: AccessCodeKind; code: string; name: string }>;
	last_incident: CardIncident | null;
	/** The stepper this viewer gets (`cardJourney`; status APPROVED, as only approved cards are here). */
	journey: CardJourney;
	actions: {
		mark_printed: boolean;
		activate: boolean;
		report_incident: boolean;
		undo_incident: boolean;
	};
}

/**
 * One batch. The NOC view (ORGANIZATION — it sees only SELF_PRINT batches
 * holding its own cards) never names a host user: every `*_by` is null and
 * `notes` too (the host's own remark).
 */
export interface PrintBatchListItem {
	print_batch_id: string;
	games_id: string;
	name: string;
	kind: PrintBatchKind;
	/** SELF_PRINT: the one organisation whose cards it holds (and who prints them); HOST: null. */
	organization: (CardRef & { code: string | null }) | null;
	status: PrintBatchStatus;
	notes: string | null;
	/** Its live cards (a card that left APPROVED since is not counted). */
	card_count: number;
	created_by: CardRef | null;
	created_at: string;
	generated_by: CardRef | null;
	generated_at: string | null;
	sent_by: CardRef | null;
	sent_at: string | null;
	printed_by: CardRef | null;
	printed_at: string | null;
}

/**
 * `GET /print-batches/:id` — the batch, its live cards per organisation and
 * per state (ONE aggregate: a batch is ≤ PRINT_BATCH_MAX cards of a handful
 * of organisations), and what this login may do — each the predicate its
 * write runs.
 */
export interface PrintBatchDetail extends PrintBatchListItem {
	organizations: Array<{
		organization: CardRef & { code: string | null };
		cards: number;
		/** Back from the printer, not received yet (PRINTED). */
		to_receive: number;
		/** Received (COLLECTED) or already activated. */
		received: number;
	}>;
	actions: {
		/** Draw the ZIP (`GET …/cards`): host printers on any batch, the NOC on its own SELF_PRINT batch. */
		generate: boolean;
		/** `POST …/generated`: the batch is OPEN, and this is a host printer on a HOST batch or the NOC on its SELF_PRINT batch (the host's download of a self-print batch is a preview). */
		mark_generated: boolean;
		send_to_printer: boolean;
		mark_printed: boolean;
		mark_received: boolean;
	};
}

/** `GET /print-batches/:id/cards` — one card as the generator draws it (`CardVisual`'s fields), plus its full-size photo. */
export interface PrintBatchCard {
	ac_card_id: string;
	card_issue_id: string;
	registration_no: number;
	printed_name: string;
	card_state: CardState;
	/** The full photo, presigned for this download (fetched by the browser with CORS); null when the card has none. */
	photo_url: string | null;
	organization: CardRef & { code: string | null };
	category: CardRef & { code: string; card_color: string | null };
	sport: CardRef | null;
	access: CardDetail["access"];
	/** The live issue's QR token: what the printed QR encodes. A self-print batch hands it to its NOC — the host delegated the printing. */
	qr_token: string;
}

export interface PrintBatchCardsPage {
	items: PrintBatchCard[];
	next_cursor: string | null;
	total: number | null;
}

/** `POST …/generated`, `…/send-to-printer`, `…/mark-printed`, `…/mark-received`: the batch after the move and the cards moved. `moved` 0 on a repeated generate (the first stamp stays). */
export interface PrintBatchMoveResult {
	print_batch: PrintBatchListItem;
	moved: number;
}

export interface PrintBatchesPage {
	items: PrintBatchListItem[];
	next_cursor: string | null;
	total: number | null;
}

/** 201. When no card of the target is printable nothing is written: 409 CARD_STATE_INVALID with `details.skipped` (same shape). */
export interface CreatePrintBatchResult {
	print_batch: PrintBatchListItem;
	skipped: Array<{ ac_card_id: string; reason: PrintBatchSkipReason }>;
}

export interface ActivationBatchListItem {
	activation_batch_id: string;
	games_id: string;
	label: string;
	valid_until: string;
	activated_count: number;
	skipped_count: number;
	created_by: CardRef | null;
	created_at: string;
}

export interface ActivationBatchesPage {
	items: ActivationBatchListItem[];
	next_cursor: string | null;
	total: number | null;
}

/** `GET /activation-batches/:id` — the batch and the cards it activated that the caller can see (≤ CARD_ACTIVATE_MAX, number order). */
export interface ActivationBatchDetail extends ActivationBatchListItem {
	cards: Array<Pick<CardListItem, "ac_card_id" | "registration_no" | "registration_no_display" | "printed_name" | "card_state" | "organization">>;
}

/** When no card of the target can be activated nothing is written: 409 CARD_STATE_INVALID with `details.skipped` (same shape). */
export interface ActivateCardsResult {
	activation_batch: ActivationBatchListItem;
	skipped: Array<{ ac_card_id: string; reason: CardActivateSkipReason }>;
}
